// Simplified main application entry point for debugging
console.log('🚀 Starting Beginner Interval Runner App...')

// import { registerSW } from 'virtual:pwa-register' // Temporarily disabled
import { ProgressTracker } from './progress-tracker.js'
import StorageManager from './storage/storage-manager.js'

// Service Worker Registration - Temporarily disabled
// const updateSW = registerSW({
//     onNeedRefresh() {
//         console.log('New content available, please refresh.')
//         if (confirm('New version available. Reload to update?')) {
//             updateSW(true)
//         }
//     },
//     onOfflineReady() {
//         console.log('App ready to work offline')
//         showOfflineReadyMessage()
//     },
//     onRegistered(r) {
//         console.log('SW Registered: ' + r)
//     },
//     onRegisterError(error) {
//         console.log('SW registration error', error)
//     }
// })

// Simple app class with minimal dependencies
class SimpleApp {
    constructor() {
        this.currentView = 'home'
        this.navigation = null
        this.timerInterval = null
        this.isWorkoutActive = false
        this.isPaused = false
        this.workoutSeconds = 0

        // Interval workout state
        this.currentProgram = null
        this.currentPhaseIndex = 0
        this.phaseStartTime = 0
        this.phases = []

        // Progress tracking
        this.storageManager = null
        this.progressTracker = null
        this.workoutHistory = []
        this.userStats = {
            totalWorkouts: 0,
            totalTime: 0,
            currentStreak: 0,
            longestStreak: 0,
            lastWorkoutDate: null
        }

        // PWA features
        this.isOffline = false
        this.deferredPrompt = null

        // Safety features
        this.safetyProfile = {
            isFirstTime: true,
            lastWorkoutDate: null,
            totalWorkouts: 0,
            painReports: [],
            restDays: []
        }

        console.log('📱 App constructor called')
    }

    async initializeProgressTracking() {
        try {
            console.log('🔧 Initializing progress tracking...')

            // Initialize storage manager
            this.storageManager = new StorageManager()
            await this.storageManager.init()

            // Initialize progress tracker
            this.progressTracker = new ProgressTracker(this.storageManager)
            await this.progressTracker.init()

            console.log('✅ Progress tracking initialized')
        } catch (error) {
            console.error('❌ Failed to initialize progress tracking:', error)
            // Continue without progress tracking if it fails
        }
    }

    initializePWA() {
        console.log('🔧 Initializing PWA features...')

        // Check for PWA installation
        this.checkPWAInstallation()

        // Setup network status listeners
        this.setupNetworkListeners()

        console.log('✅ PWA features initialized')
    }

    checkPWAInstallation() {
        // Listen for PWA install prompt
        window.addEventListener('beforeinstallprompt', (e) => {
            console.log('PWA install prompt available')
            e.preventDefault()
            this.deferredPrompt = e
            this.showInstallPrompt()
        })

        // Listen for successful installation
        window.addEventListener('appinstalled', () => {
            console.log('PWA was installed successfully')
            this.hideInstallPrompt()
            this.showNotification('App installed successfully! 🎉', 'success')
        })
    }

    showInstallPrompt() {
        // Create install button if it doesn't exist
        if (!document.getElementById('install-btn')) {
            const installBtn = document.createElement('button')
            installBtn.id = 'install-btn'
            installBtn.textContent = '📱 Install App'
            installBtn.className = 'primary-btn'
            installBtn.style.cssText = `
                position: fixed;
                bottom: 20px;
                right: 20px;
                z-index: 1000;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                animation: slideInUp 0.3s ease-out;
            `

            installBtn.addEventListener('click', async () => {
                if (this.deferredPrompt) {
                    this.deferredPrompt.prompt()
                    const { outcome } = await this.deferredPrompt.userChoice
                    console.log(`User response to install prompt: ${outcome}`)

                    if (outcome === 'accepted') {
                        this.showNotification('Installing app...', 'info')
                    }

                    this.deferredPrompt = null
                    this.hideInstallPrompt()
                }
            })

            document.body.appendChild(installBtn)
        }
    }

    hideInstallPrompt() {
        const installBtn = document.getElementById('install-btn')
        if (installBtn) {
            installBtn.style.animation = 'slideOutDown 0.3s ease-out'
            setTimeout(() => installBtn.remove(), 300)
        }
    }

    setupNetworkListeners() {
        // Set initial offline state
        this.isOffline = !navigator.onLine

        // Listen for network changes
        window.addEventListener('online', () => {
            console.log('App came online')
            this.handleOnline()
        })

        window.addEventListener('offline', () => {
            console.log('App went offline')
            this.handleOffline()
        })
    }

    handleOnline() {
        this.isOffline = false
        this.updateOfflineIndicator()
        this.updateConnectionStatus()
        this.showNotification('Connection restored! 🌐', 'success')

        // Sync any pending data if we have progress tracker
        if (this.progressTracker) {
            console.log('Syncing data after coming online...')
        }
    }

    handleOffline() {
        this.isOffline = true
        this.updateOfflineIndicator()
        this.updateConnectionStatus()
        this.showNotification('You are now offline. App will continue to work! 📱', 'warning')
    }

    setupOfflineIndicators() {
        // Create offline indicator
        const offlineIndicator = document.createElement('div')
        offlineIndicator.id = 'offline-indicator'
        offlineIndicator.innerHTML = '📡 Offline Mode'
        offlineIndicator.style.cssText = `
            position: fixed;
            top: 10px;
            left: 10px;
            background: #f59e0b;
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            z-index: 1000;
            display: none;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            animation: slideInLeft 0.3s ease-out;
        `
        document.body.appendChild(offlineIndicator)

        // Update indicator based on current state
        this.updateOfflineIndicator()
    }

    updateOfflineIndicator() {
        const indicator = document.getElementById('offline-indicator')
        if (indicator) {
            if (this.isOffline) {
                indicator.style.display = 'block'
            } else {
                indicator.style.display = 'none'
            }
        }
    }

    initializeSafety() {
        console.log('🛡️ Initializing safety features...')

        // Load safety profile from localStorage
        this.loadSafetyProfile()

        // Check for first-time user after a short delay
        setTimeout(() => {
            this.checkFirstTimeUser()
        }, 2000)

        console.log('✅ Safety features initialized')
    }

    loadSafetyProfile() {
        try {
            const saved = localStorage.getItem('safetyProfile')
            if (saved) {
                this.safetyProfile = { ...this.safetyProfile, ...JSON.parse(saved) }
            }

            // Update first-time status based on workout history
            if (this.userStats.totalWorkouts > 0) {
                this.safetyProfile.isFirstTime = false
                this.safetyProfile.totalWorkouts = this.userStats.totalWorkouts
            }
        } catch (error) {
            console.error('Failed to load safety profile:', error)
        }
    }

    saveSafetyProfile() {
        try {
            localStorage.setItem('safetyProfile', JSON.stringify(this.safetyProfile))
        } catch (error) {
            console.error('Failed to save safety profile:', error)
        }
    }

    checkFirstTimeUser() {
        if (this.safetyProfile.isFirstTime && this.userStats.totalWorkouts === 0) {
            console.log('🆕 First-time user detected, showing safety tips')
            this.showFirstTimeSafetyTips()
        }
    }

    showFirstTimeSafetyTips() {
        const modal = this.createSafetyModal('🛡️ Welcome! Safety First', 'first-time-tips')

        const content = modal.querySelector('.safety-content')
        content.innerHTML = `
            <p style="margin-bottom: 20px; color: #666; font-size: 1.1rem;">
                Welcome to interval running! Before you start, here are some essential safety tips:
            </p>

            <div class="safety-tips">
                <div class="safety-tip critical">
                    <div class="tip-icon">⚠️</div>
                    <div class="tip-content">
                        <h4>Listen to Your Body</h4>
                        <p>Stop immediately if you feel sharp pain, dizziness, or chest discomfort. Some muscle fatigue is normal, but pain is not.</p>
                    </div>
                </div>

                <div class="safety-tip">
                    <div class="tip-icon">🏃‍♀️</div>
                    <div class="tip-content">
                        <h4>Start Slow</h4>
                        <p>Begin with Week 1 program. Run at a pace where you can still hold a conversation.</p>
                    </div>
                </div>

                <div class="safety-tip">
                    <div class="tip-icon">💧</div>
                    <div class="tip-content">
                        <h4>Stay Hydrated</h4>
                        <p>Drink water before, during (if needed), and after your workout.</p>
                    </div>
                </div>

                <div class="safety-tip">
                    <div class="tip-icon">👟</div>
                    <div class="tip-content">
                        <h4>Proper Footwear</h4>
                        <p>Wear appropriate running shoes with good support and cushioning.</p>
                    </div>
                </div>

                <div class="safety-tip">
                    <div class="tip-icon">🗓️</div>
                    <div class="tip-content">
                        <h4>Rest Days Matter</h4>
                        <p>Take rest days between workouts. Your body needs time to recover and adapt.</p>
                    </div>
                </div>
            </div>

            <div style="margin-top: 20px; padding: 15px; background: #fff3cd; border-radius: 8px; border-left: 4px solid #ffc107;">
                <strong>⚡ Emergency:</strong> If you experience chest pain, severe shortness of breath, or feel faint, stop exercising immediately and seek medical attention.
            </div>
        `

        const actions = modal.querySelector('.safety-actions')
        actions.innerHTML = `
            <button class="secondary-btn" onclick="app.dismissSafetyTips()">I'll Review Later</button>
            <button class="primary-btn" onclick="app.completeSafetyTips()">Got It, Let's Start!</button>
        `

        this.showSafetyModal(modal)
    }

    completeSafetyTips() {
        this.safetyProfile.isFirstTime = false
        this.saveSafetyProfile()
        this.closeSafetyModal()
        this.showNotification('Safety tips reviewed. Stay safe and have fun! 🛡️', 'success')
    }

    dismissSafetyTips() {
        this.closeSafetyModal()
        this.showNotification('You can review safety tips anytime in Settings', 'info')
    }

    showCooldownInstructions() {
        const modal = this.createSafetyModal('🧘‍♀️ Cool Down & Stretch', 'cooldown')

        const content = modal.querySelector('.safety-content')
        content.innerHTML = `
            <p style="margin-bottom: 20px; color: #666;">
                Great workout! Now let's cool down properly to help your body recover.
            </p>

            <div class="cooldown-steps">
                <div class="cooldown-step">
                    <div class="step-number">1</div>
                    <div class="step-content">
                        <h4>Walk for 3-5 minutes</h4>
                        <p>Gradually slow your pace to bring your heart rate down</p>
                    </div>
                </div>

                <div class="cooldown-step">
                    <div class="step-number">2</div>
                    <div class="step-content">
                        <h4>Hydrate</h4>
                        <p>Drink water to replace fluids lost during exercise</p>
                    </div>
                </div>

                <div class="cooldown-step">
                    <div class="step-number">3</div>
                    <div class="step-content">
                        <h4>Stretch (Hold each for 30 seconds)</h4>
                        <ul style="margin: 8px 0; padding-left: 20px;">
                            <li>Calf stretch against a wall</li>
                            <li>Quad stretch (pull heel to glute)</li>
                            <li>Hamstring stretch (toe touches)</li>
                            <li>Hip flexor stretch (lunge position)</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div style="margin-top: 20px; padding: 15px; background: #d4edda; border-radius: 8px; border-left: 4px solid #28a745;">
                <strong>💡 Tip:</strong> Stretching while your muscles are warm helps prevent stiffness and reduces injury risk.
            </div>
        `

        const actions = modal.querySelector('.safety-actions')
        actions.innerHTML = `
            <button class="secondary-btn" onclick="app.closeSafetyModal()">Skip Cool-Down</button>
            <button class="primary-btn" onclick="app.completeCooldown()">Cool-Down Complete</button>
        `

        this.showSafetyModal(modal)
    }

    completeCooldown() {
        this.closeSafetyModal()
        this.showNotification('Excellent! Your body will thank you for cooling down properly 🙌', 'success')
    }

    checkOvertrainingRisk() {
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        // Check if user worked out yesterday
        const recentWorkouts = this.workoutHistory.filter(w => {
            const workoutDate = new Date(w.completedAt)
            return workoutDate >= yesterday && w.completed
        })

        if (recentWorkouts.length > 0) {
            this.showOvertrainingWarning()
            return true
        }

        return false
    }

    showOvertrainingWarning() {
        const modal = this.createSafetyModal('⚠️ Rest Day Recommended', 'overtraining')

        const content = modal.querySelector('.safety-content')
        content.innerHTML = `
            <p style="margin-bottom: 20px; color: #666;">
                You completed a workout recently. For beginners, rest days are crucial for recovery and preventing injury.
            </p>

            <div class="safety-alert warning">
                <h4>Why Rest Days Matter:</h4>
                <ul style="margin: 10px 0; padding-left: 20px;">
                    <li>Muscles repair and grow stronger during rest</li>
                    <li>Reduces risk of overuse injuries</li>
                    <li>Prevents burnout and maintains motivation</li>
                    <li>Improves performance in next workout</li>
                </ul>
            </div>

            <div style="margin-top: 20px; padding: 15px; background: #f8d7da; border-radius: 8px; border-left: 4px solid #dc3545;">
                <strong>Recommendation:</strong> Take today as a rest day. You can do light activities like walking or gentle stretching.
            </div>
        `

        const actions = modal.querySelector('.safety-actions')
        actions.innerHTML = `
            <button class="secondary-btn" onclick="app.continueAnyway()">Continue Anyway</button>
            <button class="primary-btn" onclick="app.takeRestDay()">Take Rest Day</button>
        `

        this.showSafetyModal(modal)
    }

    takeRestDay() {
        this.safetyProfile.restDays.push(new Date().toISOString())
        this.saveSafetyProfile()
        this.closeSafetyModal()
        this.showNotification('Smart choice! Rest days are part of training 💪', 'success')
        this.navigateTo('progress')
    }

    continueAnyway() {
        this.closeSafetyModal()
        this.showNotification('Remember to listen to your body during the workout', 'warning')
    }

    showPainReportInterface() {
        const modal = this.createSafetyModal('🩺 Report Discomfort', 'pain-report')

        const content = modal.querySelector('.safety-content')
        content.innerHTML = `
            <p style="margin-bottom: 20px; color: #666;">
                It's important to listen to your body. Please describe any discomfort you're experiencing.
            </p>

            <div class="form-group">
                <label>Pain Level:</label>
                <select id="pain-severity" class="form-control">
                    <option value="mild">Mild - Slight discomfort</option>
                    <option value="moderate">Moderate - Noticeable pain</option>
                    <option value="severe">Severe - Sharp or intense pain</option>
                </select>
            </div>

            <div class="form-group">
                <label>Location:</label>
                <select id="pain-location" class="form-control">
                    <option value="knee">Knee</option>
                    <option value="ankle">Ankle</option>
                    <option value="calf">Calf</option>
                    <option value="shin">Shin</option>
                    <option value="hip">Hip</option>
                    <option value="back">Back</option>
                    <option value="chest">Chest</option>
                    <option value="other">Other</option>
                </select>
            </div>

            <div class="form-group">
                <label style="display: flex; align-items: center; gap: 8px;">
                    <input type="checkbox" id="sharp-pain">
                    <span>The pain is sharp or stabbing</span>
                </label>
            </div>
        `

        const actions = modal.querySelector('.safety-actions')
        actions.innerHTML = `
            <button class="secondary-btn" onclick="app.closeSafetyModal()">Cancel</button>
            <button class="danger-btn" onclick="app.submitPainReport()">Report Pain</button>
        `

        this.showSafetyModal(modal)
    }

    submitPainReport() {
        const severity = document.getElementById('pain-severity').value
        const location = document.getElementById('pain-location').value
        const sharp = document.getElementById('sharp-pain').checked

        const painReport = {
            severity,
            location,
            sharp,
            timestamp: new Date().toISOString()
        }

        this.safetyProfile.painReports.push(painReport)
        this.saveSafetyProfile()

        this.closeSafetyModal()

        // Show appropriate response based on severity
        if (severity === 'severe' || sharp) {
            this.showSeverePainResponse(painReport)
        } else {
            this.showMildPainResponse(painReport)
        }
    }

    showSeverePainResponse(painReport) {
        const modal = this.createSafetyModal('🚨 Immediate Action Required', 'severe-pain')

        const content = modal.querySelector('.safety-content')
        content.innerHTML = `
            <div class="safety-alert critical">
                <h4>⚠️ Stop Exercising Immediately</h4>
                <p>Severe or sharp pain is a warning sign that should not be ignored.</p>
            </div>

            <div class="recommendations">
                <h4>Immediate Steps:</h4>
                <ol style="margin: 10px 0; padding-left: 20px;">
                    <li>Stop all physical activity</li>
                    <li>Apply ice if there's swelling</li>
                    <li>Elevate the affected area if possible</li>
                    <li>Avoid putting weight on the injured area</li>
                </ol>
            </div>

            <div style="margin-top: 20px; padding: 15px; background: #f8d7da; border-radius: 8px; border-left: 4px solid #dc3545;">
                <strong>⚕️ Medical Attention:</strong> Consider seeing a healthcare provider, especially if pain persists or worsens.
            </div>
        `

        const actions = modal.querySelector('.safety-actions')
        actions.innerHTML = `
            <button class="primary-btn" onclick="app.acknowledgeWarning()">I Understand</button>
        `

        this.showSafetyModal(modal)
    }

    showMildPainResponse(painReport) {
        const modal = this.createSafetyModal('💡 Pain Management Tips', 'mild-pain')

        const content = modal.querySelector('.safety-content')
        content.innerHTML = `
            <p style="margin-bottom: 20px; color: #666;">
                Mild discomfort can be normal when starting a new exercise routine, but it's important to monitor it.
            </p>

            <div class="recommendations">
                <h4>What You Can Do:</h4>
                <ul style="margin: 10px 0; padding-left: 20px;">
                    <li>Take a rest day or reduce intensity</li>
                    <li>Apply ice for 15-20 minutes if there's swelling</li>
                    <li>Gentle stretching may help</li>
                    <li>Stay hydrated and get adequate sleep</li>
                </ul>
            </div>

            <div style="margin-top: 20px; padding: 15px; background: #fff3cd; border-radius: 8px; border-left: 4px solid #ffc107;">
                <strong>Monitor:</strong> If pain worsens or doesn't improve with rest, consider consulting a healthcare provider.
            </div>
        `

        const actions = modal.querySelector('.safety-actions')
        actions.innerHTML = `
            <button class="secondary-btn" onclick="app.closeSafetyModal()">Take Rest Day</button>
            <button class="primary-btn" onclick="app.acknowledgeWarning()">Continue Carefully</button>
        `

        this.showSafetyModal(modal)
    }

    acknowledgeWarning() {
        this.closeSafetyModal()
        this.showNotification('Take care of yourself. Your health comes first! 🏥', 'info')
    }

    async init() {
        console.log('🔧 Initializing app...')

        try {
            // Initialize PWA features
            this.initializePWA()

            // Initialize storage and progress tracking
            await this.initializeProgressTracking()

            // Load saved data first
            await this.loadProgressData()

            // Initialize navigation first
            this.initNavigation()

            // Initialize basic components
            this.initBasicComponents()

            // Setup offline indicators
            this.setupOfflineIndicators()

            // Initialize safety features
            this.initializeSafety()

            // Update home stats
            this.updateHomeStats()

            console.log('✅ App initialized successfully')
        } catch (error) {
            console.error('❌ App initialization failed:', error)
        }
    }

    initNavigation() {
        console.log('🧭 Setting up navigation...')

        // Simple navigation without external dependencies
        const navButtons = document.querySelectorAll('.nav-btn')

        navButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault()
                const route = e.target.dataset.route
                console.log(`📍 Navigating to: ${route}`)
                this.navigateTo(route)
            })
        })

        // Handle hero buttons
        const browseBtn = document.querySelector('.hero-actions .primary-btn')
        const quickStartBtn = document.querySelector('.hero-actions .secondary-btn')

        if (browseBtn) {
            browseBtn.onclick = () => {
                console.log('📋 Browse Programs clicked')
                this.navigateTo('programs')
            }
        }

        if (quickStartBtn) {
            quickStartBtn.onclick = () => {
                console.log('⚡ Quick Start clicked')
                this.navigateTo('workout')
            }
        }

        // Update hero buttons to work with simple app
        setTimeout(() => {
            const heroButtons = document.querySelectorAll('.hero-actions button')
            heroButtons.forEach(btn => {
                if (btn.textContent.includes('Browse Programs')) {
                    btn.onclick = () => this.navigateTo('programs')
                } else if (btn.textContent.includes('Quick Start')) {
                    btn.onclick = () => this.navigateTo('workout')
                }
            })
        }, 100)

        console.log('✅ Navigation setup complete')
    }

    navigateTo(route) {
        console.log(`🎯 Navigating to route: ${route}`)

        // Update navigation state
        this.currentView = route

        // Update nav button states
        document.querySelectorAll('.nav-btn').forEach(btn => {
            btn.classList.remove('active')
        })

        const activeBtn = document.querySelector(`[data-route="${route}"]`)
        if (activeBtn) {
            activeBtn.classList.add('active')
        }

        // Show/hide views
        document.querySelectorAll('.view').forEach(view => {
            view.classList.remove('active')
        })

        const targetView = document.getElementById(`${route}-view`)
        if (targetView) {
            targetView.classList.add('active')
            console.log(`✅ Showing ${route} view`)
        } else {
            console.warn(`⚠️ View not found: ${route}-view`)
        }

        // Update page title
        document.title = `${route.charAt(0).toUpperCase() + route.slice(1)} - Beginner Interval Runner`

        // Refresh data for specific views
        if (route === 'progress') {
            this.updateProgressView()
        } else if (route === 'settings') {
            // Update connection status when viewing settings
            setTimeout(() => this.updateConnectionStatus(), 100)
        }
    }

    initBasicComponents() {
        console.log('🔧 Initializing basic components...')

        // Add basic content to views
        this.setupProgramsView()
        this.setupWorkoutView()
        this.setupProgressView()
        this.setupSettingsView()

        console.log('✅ Basic components initialized')

        // Setup audio control listeners
        this.setupAudioControls()
    }

    updateHomeStats() {
        // Update home view statistics
        const totalWorkoutsEl = document.getElementById('home-total-workouts')
        const currentStreakEl = document.getElementById('home-current-streak')

        if (totalWorkoutsEl) {
            totalWorkoutsEl.textContent = this.userStats.totalWorkouts.toString()
        }

        if (currentStreakEl) {
            currentStreakEl.textContent = this.userStats.currentStreak.toString()
        }

        console.log('📊 Home stats updated:', {
            totalWorkouts: this.userStats.totalWorkouts,
            currentStreak: this.userStats.currentStreak
        })
    }

    setupAudioControls() {
        // Volume slider
        document.addEventListener('input', (e) => {
            if (e.target.id === 'volume-slider') {
                const volumeDisplay = document.getElementById('volume-display')
                if (volumeDisplay) {
                    volumeDisplay.textContent = `${e.target.value}%`
                }
            }
        })

        // Audio enabled checkbox
        document.addEventListener('change', (e) => {
            if (e.target.id === 'audio-enabled') {
                const enabled = e.target.checked
                console.log(`🔊 Audio ${enabled ? 'enabled' : 'disabled'}`)
                if (enabled) {
                    this.testAudio()
                }
            }
        })
    }

    setupProgramsView() {
        const programsGrid = document.querySelector('.programs-grid')
        if (programsGrid) {
            programsGrid.innerHTML = `
                <div class="program-section">
                    <h2>Training Programs</h2>
                    <p style="text-align: center; margin-bottom: var(--spacing-lg); color: var(--text-secondary);">
                        Choose a program that matches your fitness level. Start with Week 1 if you're new to running!
                    </p>

                    <h3>🏃‍♀️ Couch to 5K Program</h3>
                    <div class="program-cards">
                        <div class="program-card featured">
                            <h4>📅 Week 1 - Getting Started</h4>
                            <p><strong>Run:</strong> 1 minute | <strong>Walk:</strong> 3 minutes</p>
                            <p><strong>Intervals:</strong> 6 rounds | <strong>Total:</strong> ~29 minutes</p>
                            <p>Perfect for absolute beginners. Build your running foundation with short, manageable intervals.</p>
                            <button class="primary-btn program-btn" onclick="app.selectProgram('week-1')">Start Week 1</button>
                        </div>

                        <div class="program-card success">
                            <h4>📅 Week 2 - Building Endurance</h4>
                            <p><strong>Run:</strong> 2 minutes | <strong>Walk:</strong> 3 minutes</p>
                            <p><strong>Intervals:</strong> 5 rounds | <strong>Total:</strong> ~30 minutes</p>
                            <p>Ready for longer runs? Build endurance with extended running intervals.</p>
                            <button class="primary-btn program-btn" onclick="app.selectProgram('week-2')" style="background: var(--success-color);">Start Week 2</button>
                        </div>

                        <div class="program-card">
                            <h4>📅 Week 3 - More Progress</h4>
                            <p><strong>Run:</strong> 3 minutes | <strong>Walk:</strong> 2 minutes</p>
                            <p><strong>Intervals:</strong> 5 rounds | <strong>Total:</strong> ~30 minutes</p>
                            <p>Step up to longer running intervals with shorter recovery periods.</p>
                            <button class="primary-btn program-btn" onclick="app.selectProgram('week-3')" style="background: #6366f1;">Start Week 3</button>
                        </div>
                    </div>

                    <h3>🧪 Test & Practice Programs</h3>
                    <div class="program-cards">
                        <div class="program-card">
                            <h4>🏃‍♂️ Quick Test Intervals</h4>
                            <p><strong>Run:</strong> 5 seconds | <strong>Walk:</strong> 10 seconds</p>
                            <p><strong>Intervals:</strong> 3 rounds | <strong>Total:</strong> ~1 minute</p>
                            <p>Perfect for testing the app functionality!</p>
                            <button class="secondary-btn program-btn" onclick="app.selectProgram('quick-test')">Test Run</button>
                        </div>

                        <div class="program-card">
                            <h4>🚶‍♀️ Beginner Practice</h4>
                            <p><strong>Run:</strong> 30 seconds | <strong>Walk:</strong> 60 seconds</p>
                            <p><strong>Intervals:</strong> 4 rounds | <strong>Total:</strong> ~6 minutes</p>
                            <p>Gentle introduction to interval training.</p>
                            <button class="secondary-btn program-btn" onclick="app.selectProgram('beginner-intervals')">Try It</button>
                        </div>

                        <div class="program-card">
                            <h4>⏱️ Simple Intervals</h4>
                            <p><strong>Run:</strong> 2 minutes | <strong>Walk:</strong> 1 minute</p>
                            <p><strong>Intervals:</strong> 5 rounds | <strong>Total:</strong> ~15 minutes</p>
                            <p>Basic interval training without warmup/cooldown.</p>
                            <button class="secondary-btn program-btn" onclick="app.selectProgram('simple-intervals')">Start</button>
                        </div>
                    </div>

                    <div class="tips-section">
                        <h4>💡 Getting Started Tips</h4>
                        <ul>
                            <li>Always start with a 5-minute warm-up walk</li>
                            <li>Run at a pace where you can still hold a conversation</li>
                            <li>Complete each workout 3 times per week with rest days between</li>
                            <li>Listen to your body - it's okay to repeat a week if needed</li>
                            <li>Stay hydrated and wear proper running shoes</li>
                        </ul>
                    </div>
                </div>
            `
        }
    }

    setupWorkoutView() {
        const workoutInterface = document.querySelector('.workout-interface')
        if (workoutInterface) {
            workoutInterface.innerHTML = `
                <div style="padding: 20px; text-align: center;">
                    <h2>Workout Interface</h2>

                    <div style="margin: 20px 0; padding: 20px; background: #f8f9fa; border-radius: 8px;">
                        <div style="font-size: 3rem; font-weight: bold; color: #007bff; margin-bottom: 10px;">
                            <span id="timer-minutes">00</span>:<span id="timer-seconds">00</span>
                        </div>
                        <div id="timer-label">Ready to Start</div>
                    </div>

                    <div style="margin: 20px 0;">
                        <button id="start-btn" class="primary-btn" onclick="app.startWorkout()" style="margin: 5px;">Start Workout</button>
                        <button id="pause-btn" class="secondary-btn" onclick="app.pauseWorkout()" style="margin: 5px;" disabled>Pause</button>
                        <button id="resume-btn" class="secondary-btn" onclick="app.resumeWorkout()" style="margin: 5px; display: none;">Resume</button>
                        <button id="stop-btn" class="secondary-btn" onclick="app.stopWorkout()" style="margin: 5px;" disabled>Stop</button>
                    </div>

                    <div id="phase-info" style="margin: 20px 0; padding: 15px; background: #e3f2fd; border-radius: 8px; display: none;">
                        <h3 id="current-phase">Current Phase</h3>
                        <div id="phase-timer" style="font-size: 1.5rem; font-weight: bold; color: #1976d2; margin: 10px 0;">00:00</div>
                        <div id="phase-instruction">Get ready...</div>
                        <div style="margin-top: 10px;">
                            <div id="next-phase-info" style="font-size: 0.9rem; color: #666;">Next: --</div>
                        </div>
                    </div>

                    <div id="program-info" style="margin-top: 20px; padding: 15px; background: #e9ecef; border-radius: 8px;">
                        <p>Select a program from the Programs tab to begin your workout.</p>
                    </div>
                </div>
            `
        }
    }

    setupProgressView() {
        this.updateProgressView()
    }

    updateProgressView() {
        const progressDashboard = document.querySelector('.progress-dashboard')
        if (progressDashboard) {
            const totalHours = Math.floor(this.userStats.totalTime / 3600)
            const totalMinutes = Math.floor((this.userStats.totalTime % 3600) / 60)
            const timeDisplay = totalHours > 0 ? `${totalHours}h ${totalMinutes}m` : `${totalMinutes}m`

            progressDashboard.innerHTML = `
                <div style="padding: 20px;">
                    <h2>Progress Dashboard</h2>
                    <p>Track your running journey and celebrate your achievements!</p>

                    <div style="margin-top: 20px; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
                        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #007bff;">
                            <h3>Total Workouts</h3>
                            <div style="font-size: 2rem; font-weight: bold; color: #007bff;">${this.userStats.totalWorkouts}</div>
                        </div>
                        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745;">
                            <h3>Current Streak</h3>
                            <div style="font-size: 2rem; font-weight: bold; color: #28a745;">${this.userStats.currentStreak}</div>
                            <div style="font-size: 0.9rem; color: #666;">days</div>
                        </div>
                        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #ffc107;">
                            <h3>Total Time</h3>
                            <div style="font-size: 2rem; font-weight: bold; color: #ffc107;">${timeDisplay}</div>
                        </div>
                        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #dc3545;">
                            <h3>Longest Streak</h3>
                            <div style="font-size: 2rem; font-weight: bold; color: #dc3545;">${this.userStats.longestStreak}</div>
                            <div style="font-size: 0.9rem; color: #666;">days</div>
                        </div>
                    </div>

                    ${this.renderRecentWorkouts()}
                    ${this.renderAchievements()}
                </div>
            `
        }
    }

    renderRecentWorkouts() {
        const recentWorkouts = this.workoutHistory
            .filter(w => w.completed)
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
            .slice(0, 5)

        if (recentWorkouts.length === 0) {
            return `
                <div style="margin-top: 30px; padding: 20px; background: #e9ecef; border-radius: 8px; text-align: center;">
                    <h3>Recent Workouts</h3>
                    <p>No workouts completed yet. Start your first workout to see your progress!</p>
                </div>
            `
        }

        const workoutsList = recentWorkouts.map(workout => {
            const date = new Date(workout.completedAt).toLocaleDateString()
            const duration = this.formatDuration(workout.duration)
            const programName = workout.metadata?.programName || workout.programId

            return `
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: white; border-radius: 6px; margin-bottom: 8px; border-left: 3px solid #28a745;">
                    <div>
                        <div style="font-weight: bold; color: #333;">${programName}</div>
                        <div style="font-size: 0.9rem; color: #666;">${date}</div>
                    </div>
                    <div style="text-align: right;">
                        <div style="font-weight: bold; color: #007bff;">${duration}</div>
                        <div style="font-size: 0.8rem; color: #28a745;">✅ Completed</div>
                    </div>
                </div>
            `
        }).join('')

        return `
            <div style="margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
                <h3>Recent Workouts</h3>
                <div style="margin-top: 15px;">
                    ${workoutsList}
                </div>
            </div>
        `
    }

    renderAchievements() {
        // Simple achievements based on current stats
        const achievements = [
            {
                name: 'First Steps',
                description: 'Complete your first workout',
                icon: '🎯',
                unlocked: this.userStats.totalWorkouts >= 1
            },
            {
                name: 'Getting Started',
                description: 'Complete 3 workouts',
                icon: '🏃‍♂️',
                unlocked: this.userStats.totalWorkouts >= 3
            },
            {
                name: 'Week Warrior',
                description: 'Complete 7 workouts',
                icon: '⚡',
                unlocked: this.userStats.totalWorkouts >= 7
            },
            {
                name: 'Streak Starter',
                description: 'Maintain a 3-day streak',
                icon: '🔥',
                unlocked: this.userStats.currentStreak >= 3 || this.userStats.longestStreak >= 3
            },
            {
                name: 'Consistency King',
                description: 'Maintain a 7-day streak',
                icon: '👑',
                unlocked: this.userStats.currentStreak >= 7 || this.userStats.longestStreak >= 7
            },
            {
                name: 'Time Keeper',
                description: 'Complete 1 hour of training',
                icon: '⏰',
                unlocked: this.userStats.totalTime >= 3600
            }
        ]

        const achievementsList = achievements.map(achievement => {
            const statusClass = achievement.unlocked ? 'unlocked' : 'locked'
            const opacity = achievement.unlocked ? '1' : '0.5'
            const bgColor = achievement.unlocked ? '#e8f5e8' : '#f5f5f5'
            const borderColor = achievement.unlocked ? '#28a745' : '#ddd'

            return `
                <div style="display: flex; align-items: center; gap: 15px; padding: 12px; background: ${bgColor}; border-radius: 6px; margin-bottom: 8px; border-left: 3px solid ${borderColor}; opacity: ${opacity};">
                    <div style="font-size: 1.5rem;">${achievement.icon}</div>
                    <div style="flex: 1;">
                        <div style="font-weight: bold; color: #333;">${achievement.name}</div>
                        <div style="font-size: 0.9rem; color: #666;">${achievement.description}</div>
                    </div>
                    ${achievement.unlocked ? '<div style="color: #28a745; font-weight: bold;">✓</div>' : ''}
                </div>
            `
        }).join('')

        return `
            <div style="margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
                <h3>Achievements</h3>
                <div style="margin-top: 15px;">
                    ${achievementsList}
                </div>
            </div>
        `
    }

    formatDuration(seconds) {
        if (seconds < 60) {
            return `${seconds}s`
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60)
            const remainingSeconds = seconds % 60
            return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
        } else {
            const hours = Math.floor(seconds / 3600)
            const minutes = Math.floor((seconds % 3600) / 60)
            return `${hours}h ${minutes}m`
        }
    }

    setupSettingsView() {
        const settingsPanel = document.querySelector('.settings-panel')
        if (settingsPanel) {
            settingsPanel.innerHTML = `
                <div style="padding: 20px;">
                    <h2>Settings</h2>

                    <div style="margin-top: 20px;">
                        <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
                            <h3>Audio Settings</h3>
                            <label style="display: block; margin: 10px 0;">
                                <input type="checkbox" checked id="audio-enabled"> Enable audio cues
                            </label>
                            <label style="display: block; margin: 10px 0;">
                                Volume: <input type="range" min="0" max="100" value="80" id="volume-slider" style="margin-left: 10px;">
                                <span id="volume-display">80%</span>
                            </label>
                            <button class="secondary-btn" onclick="app.testAudio()" style="margin-top: 10px;">Test Audio</button>
                        </div>

                        <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
                            <h3>Workout Preferences</h3>
                            <label style="display: block; margin: 10px 0;">
                                <input type="checkbox"> Show countdown timer
                            </label>
                            <label style="display: block; margin: 10px 0;">
                                <input type="checkbox"> Vibrate on phase changes
                            </label>
                        </div>

                        <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
                            <h3>App Settings</h3>
                            <div style="margin: 10px 0;">
                                <strong>Connection Status:</strong>
                                <span id="connection-status" style="color: ${this.isOffline ? '#dc3545' : '#28a745'};">
                                    ${this.isOffline ? '📡 Offline' : '🌐 Online'}
                                </span>
                            </div>
                            <button class="secondary-btn" onclick="app.manualInstallPrompt()" style="margin-top: 10px;">
                                📱 Install as App
                            </button>
                            <p style="font-size: 0.9rem; color: #666; margin-top: 8px;">
                                Install this app on your device for the best experience and offline access.
                            </p>
                        </div>

                        <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
                            <h3>Safety & Health</h3>
                            <button class="secondary-btn" onclick="app.showFirstTimeSafetyTips()" style="margin: 5px; width: 100%;">
                                🛡️ Review Safety Tips
                            </button>
                            <button class="secondary-btn" onclick="app.showPainReportInterface()" style="margin: 5px; width: 100%;">
                                🩺 Report Pain/Discomfort
                            </button>
                            <button class="secondary-btn" onclick="app.showCooldownInstructions()" style="margin: 5px; width: 100%;">
                                🧘‍♀️ Cool-Down Guide
                            </button>
                            <p style="font-size: 0.9rem; color: #666; margin-top: 8px;">
                                Your safety is our priority. Use these tools to stay healthy while training.
                            </p>
                        </div>
                    </div>
                </div>
            `
        }
    }

    // Program definitions
    getPrograms() {
        return {
            'week-1': {
                id: 'week-1',
                name: '📅 Week 1 - Couch to 5K',
                description: 'Run 1 minute, walk 3 minutes (6 intervals)',
                difficulty: 'beginner',
                phases: [
                    { type: 'warmup', duration: 300, instruction: 'Start with 5 minutes of brisk walking to warm up' },
                    { type: 'run', duration: 60, instruction: 'RUN for 1 minute at a comfortable pace' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 60, instruction: 'RUN for 1 minute - interval 2 of 6' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 60, instruction: 'RUN for 1 minute - interval 3 of 6' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 60, instruction: 'RUN for 1 minute - interval 4 of 6' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 60, instruction: 'RUN for 1 minute - interval 5 of 6' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 60, instruction: 'RUN for 1 minute - final interval!' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to finish - great job!' }
                ]
            },
            'week-2': {
                id: 'week-2',
                name: '📅 Week 2 - Couch to 5K',
                description: 'Run 2 minutes, walk 3 minutes (5 intervals)',
                difficulty: 'beginner',
                phases: [
                    { type: 'warmup', duration: 300, instruction: 'Start with 5 minutes of brisk walking to warm up' },
                    { type: 'run', duration: 120, instruction: 'RUN for 2 minutes at a comfortable pace' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN for 2 minutes - interval 2 of 5' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN for 2 minutes - interval 3 of 5' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN for 2 minutes - interval 4 of 5' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN for 2 minutes - final interval!' },
                    { type: 'walk', duration: 180, instruction: 'Walk for 3 minutes to finish - excellent work!' }
                ]
            },
            'week-3': {
                id: 'week-3',
                name: '📅 Week 3 - Couch to 5K',
                description: 'Run 3 minutes, walk 2 minutes (5 intervals)',
                difficulty: 'beginner',
                phases: [
                    { type: 'warmup', duration: 300, instruction: 'Start with 5 minutes of brisk walking to warm up' },
                    { type: 'run', duration: 180, instruction: 'RUN for 3 minutes at a comfortable pace' },
                    { type: 'walk', duration: 120, instruction: 'Walk for 2 minutes to recover' },
                    { type: 'run', duration: 180, instruction: 'RUN for 3 minutes - interval 2 of 5' },
                    { type: 'walk', duration: 120, instruction: 'Walk for 2 minutes to recover' },
                    { type: 'run', duration: 180, instruction: 'RUN for 3 minutes - interval 3 of 5' },
                    { type: 'walk', duration: 120, instruction: 'Walk for 2 minutes to recover' },
                    { type: 'run', duration: 180, instruction: 'RUN for 3 minutes - interval 4 of 5' },
                    { type: 'walk', duration: 120, instruction: 'Walk for 2 minutes to recover' },
                    { type: 'run', duration: 180, instruction: 'RUN for 3 minutes - final interval!' },
                    { type: 'walk', duration: 120, instruction: 'Walk for 2 minutes to finish - amazing progress!' }
                ]
            },
            'quick-test': {
                id: 'quick-test',
                name: '🏃‍♂️ Quick Test Intervals',
                description: 'Run 5 seconds, walk 10 seconds (3 rounds) - Perfect for testing!',
                difficulty: 'test',
                phases: [
                    { type: 'warmup', duration: 3, instruction: 'Get ready to start!' },
                    { type: 'run', duration: 5, instruction: 'RUN! Keep a steady pace' },
                    { type: 'walk', duration: 10, instruction: 'Walk and recover' },
                    { type: 'run', duration: 5, instruction: 'RUN! Second interval' },
                    { type: 'walk', duration: 10, instruction: 'Walk and recover' },
                    { type: 'run', duration: 5, instruction: 'RUN! Final interval' },
                    { type: 'cooldown', duration: 5, instruction: 'Cool down - great job!' }
                ]
            },
            'beginner-intervals': {
                id: 'beginner-intervals',
                name: '🚶‍♀️ Beginner Intervals',
                description: 'Run 10 seconds, walk 20 seconds (2 rounds)',
                difficulty: 'beginner',
                phases: [
                    { type: 'warmup', duration: 5, instruction: 'Warm up with gentle movement' },
                    { type: 'run', duration: 10, instruction: 'Run at comfortable pace' },
                    { type: 'walk', duration: 20, instruction: 'Walk to recover' },
                    { type: 'run', duration: 10, instruction: 'Run - second round!' },
                    { type: 'walk', duration: 20, instruction: 'Final recovery walk' },
                    { type: 'cooldown', duration: 10, instruction: 'Cool down and stretch' }
                ]
            },
            'simple-intervals': {
                id: 'simple-intervals',
                name: '⚡ Simple Intervals',
                description: 'Run 2 minutes, walk 1 minute (5 rounds)',
                difficulty: 'beginner',
                phases: [
                    { type: 'run', duration: 120, instruction: 'RUN at moderate pace' },
                    { type: 'walk', duration: 60, instruction: 'Walk to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN - interval 2 of 5' },
                    { type: 'walk', duration: 60, instruction: 'Walk to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN - interval 3 of 5' },
                    { type: 'walk', duration: 60, instruction: 'Walk to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN - interval 4 of 5' },
                    { type: 'walk', duration: 60, instruction: 'Walk to recover' },
                    { type: 'run', duration: 120, instruction: 'RUN - final interval!' },
                    { type: 'walk', duration: 60, instruction: 'Walk to finish - well done!' }
                ]
            },
            'demo-workout': {
                id: 'demo-workout',
                name: '⏱️ Demo Workout',
                description: 'Simple 60-second workout',
                difficulty: 'test',
                phases: [
                    { type: 'workout', duration: 60, instruction: 'Simple 60-second workout' }
                ]
            }
        }
    }

    async loadProgressData() {
        try {
            if (this.progressTracker) {
                console.log('📊 Loading progress data from tracker...')
                const progress = await this.progressTracker.getProgress()

                // Update user stats from progress tracker
                this.userStats = {
                    totalWorkouts: progress.stats.totalWorkouts,
                    totalTime: progress.stats.totalDuration,
                    currentStreak: progress.stats.currentStreak,
                    longestStreak: progress.stats.longestStreak,
                    lastWorkoutDate: progress.stats.lastWorkout
                }

                // Get workout history
                this.workoutHistory = await this.progressTracker.storage.getWorkouts()

                console.log('✅ Progress data loaded:', this.userStats)
            } else {
                console.log('⚠️ Progress tracker not available, using defaults')
                // Load from localStorage as fallback
                const savedStats = localStorage.getItem('userStats')
                if (savedStats) {
                    this.userStats = JSON.parse(savedStats)
                }

                const savedHistory = localStorage.getItem('workoutHistory')
                if (savedHistory) {
                    this.workoutHistory = JSON.parse(savedHistory)
                }
            }
        } catch (error) {
            console.error('❌ Failed to load progress data:', error)
        }
    }

    async saveProgressData() {
        try {
            // Save to localStorage as backup
            localStorage.setItem('userStats', JSON.stringify(this.userStats))
            localStorage.setItem('workoutHistory', JSON.stringify(this.workoutHistory))

            console.log('💾 Progress data saved to localStorage')
        } catch (error) {
            console.error('❌ Failed to save progress data:', error)
        }
    }

    // Simple workout methods
    selectProgram(programId) {
        const programs = this.getPrograms()
        this.currentProgram = programs[programId]

        if (!this.currentProgram) {
            console.log(`❌ Program not found: ${programId}`)
            this.showNotification(`Program not found: ${programId}`, 'error')
            return
        }

        console.log(`📋 Selected program: ${this.currentProgram.name}`)
        this.showNotification(`Selected: ${this.currentProgram.name}`, 'success')

        // Update program info
        const programInfo = document.getElementById('program-info')
        if (programInfo) {
            const totalDuration = this.currentProgram.phases.reduce((sum, phase) => sum + phase.duration, 0)
            const runPhases = this.currentProgram.phases.filter(p => p.type === 'run')
            const totalRunTime = runPhases.reduce((sum, phase) => sum + phase.duration, 0)

            programInfo.innerHTML = `
                <div style="text-align: left; background: #e3f2fd; padding: 20px; border-radius: 8px; border-left: 4px solid #2196f3;">
                    <h3 style="margin: 0 0 15px 0; color: #1976d2;">${this.currentProgram.name}</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px; margin-bottom: 15px;">
                        <div>
                            <strong>Total Duration:</strong><br>
                            <span style="color: #1976d2; font-size: 1.1rem;">${this.formatDuration(totalDuration)}</span>
                        </div>
                        <div>
                            <strong>Running Time:</strong><br>
                            <span style="color: #d32f2f; font-size: 1.1rem;">${this.formatDuration(totalRunTime)}</span>
                        </div>
                        <div>
                            <strong>Intervals:</strong><br>
                            <span style="color: #388e3c; font-size: 1.1rem;">${runPhases.length} rounds</span>
                        </div>
                    </div>
                    ${this.currentProgram.description ? `<p style="margin: 10px 0; color: #666; font-style: italic;">${this.currentProgram.description}</p>` : ''}
                    <div style="margin-top: 15px; padding: 10px; background: rgba(76, 175, 80, 0.1); border-radius: 6px; border-left: 3px solid #4caf50;">
                        <strong style="color: #2e7d32;">✅ Ready to start!</strong>
                        <p style="margin: 5px 0 0 0; font-size: 0.9rem; color: #2e7d32;">Click "Start Workout" when you're ready to begin.</p>
                    </div>
                </div>
            `
        }

        this.navigateTo('workout')
    }

    startWorkout() {
        if (this.isWorkoutActive && !this.isPaused) {
            console.log('⚠️ Workout already active')
            return
        }

        if (!this.currentProgram) {
            this.showNotification('Please select a program first!', 'warning')
            this.navigateTo('programs')
            return
        }

        // Safety check before starting workout
        if (this.checkOvertrainingRisk()) {
            return // Don't start workout if overtraining risk detected
        }

        console.log('▶️ Starting workout...')
        this.showNotification('Workout started!', 'success')

        this.isWorkoutActive = true
        this.isPaused = false
        this.workoutSeconds = 0
        this.currentPhaseIndex = 0
        this.phaseStartTime = 0
        this.phases = [...this.currentProgram.phases]

        this.updateButtonStates()
        this.startPhase()
        this.startTimer()
    }

    startTimer() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval)
        }

        this.timerInterval = setInterval(() => {
            if (!this.isPaused && this.isWorkoutActive) {
                this.workoutSeconds++
                this.updateTimerDisplay()
                this.updatePhaseDisplay()

                // Check if current phase is complete
                const currentPhase = this.phases[this.currentPhaseIndex]
                if (currentPhase) {
                    const phaseElapsed = this.workoutSeconds - this.phaseStartTime
                    if (phaseElapsed >= currentPhase.duration) {
                        this.completePhase()
                    }
                }
            }
        }, 1000)
    }

    updateTimerDisplay() {
        const minutes = Math.floor(this.workoutSeconds / 60)
        const seconds = this.workoutSeconds % 60

        const minutesEl = document.getElementById('timer-minutes')
        const secondsEl = document.getElementById('timer-seconds')

        if (minutesEl) minutesEl.textContent = minutes.toString().padStart(2, '0')
        if (secondsEl) secondsEl.textContent = seconds.toString().padStart(2, '0')
    }

    pauseWorkout() {
        if (!this.isWorkoutActive || this.isPaused) {
            console.log('⚠️ No active workout to pause')
            return
        }

        console.log('⏸️ Pausing workout...')
        this.isPaused = true
        this.showNotification('Workout paused', 'warning')
        this.updateButtonStates()
    }

    resumeWorkout() {
        if (!this.isWorkoutActive || !this.isPaused) {
            console.log('⚠️ No paused workout to resume')
            return
        }

        console.log('▶️ Resuming workout...')
        this.isPaused = false
        this.showNotification('Workout resumed', 'success')
        this.updateButtonStates()
    }

    stopWorkout() {
        if (!this.isWorkoutActive) {
            console.log('⚠️ No active workout to stop')
            return
        }

        console.log('⏹️ Stopping workout...')

        this.isWorkoutActive = false
        this.isPaused = false

        if (this.timerInterval) {
            clearInterval(this.timerInterval)
            this.timerInterval = null
        }

        this.showNotification(`Workout stopped after ${Math.floor(this.workoutSeconds / 60)}:${(this.workoutSeconds % 60).toString().padStart(2, '0')}`, 'info')
        this.updateButtonStates()
        this.resetTimer()
    }

    resetTimer() {
        this.workoutSeconds = 0
        this.updateTimerDisplay()
    }

    updateButtonStates() {
        const startBtn = document.getElementById('start-btn')
        const pauseBtn = document.getElementById('pause-btn')
        const resumeBtn = document.getElementById('resume-btn')
        const stopBtn = document.getElementById('stop-btn')

        if (startBtn && pauseBtn && resumeBtn && stopBtn) {
            if (!this.isWorkoutActive) {
                // Not active - show start button
                startBtn.disabled = false
                startBtn.style.display = 'inline-block'
                pauseBtn.disabled = true
                pauseBtn.style.display = 'inline-block'
                resumeBtn.style.display = 'none'
                stopBtn.disabled = true
            } else if (this.isPaused) {
                // Paused - show resume button
                startBtn.disabled = true
                startBtn.style.display = 'none'
                pauseBtn.disabled = true
                pauseBtn.style.display = 'none'
                resumeBtn.style.display = 'inline-block'
                stopBtn.disabled = false
            } else {
                // Active - show pause button
                startBtn.disabled = true
                startBtn.style.display = 'none'
                pauseBtn.disabled = false
                pauseBtn.style.display = 'inline-block'
                resumeBtn.style.display = 'none'
                stopBtn.disabled = false
            }
        }
    }

    startPhase() {
        const currentPhase = this.phases[this.currentPhaseIndex]
        if (!currentPhase) {
            this.completeWorkout()
            return
        }

        this.phaseStartTime = this.workoutSeconds
        console.log(`🎯 Starting phase: ${currentPhase.type} (${currentPhase.duration}s)`)
        this.showNotification(`${currentPhase.type.toUpperCase()}: ${currentPhase.instruction}`, 'info')

        // Play audio cue for phase transition
        this.playAudioCue(currentPhase)

        // Show phase info
        const phaseInfo = document.getElementById('phase-info')
        if (phaseInfo) {
            phaseInfo.style.display = 'block'
        }

        this.updatePhaseDisplay()
    }

    completePhase() {
        const completedPhase = this.phases[this.currentPhaseIndex]
        console.log(`✅ Completed phase: ${completedPhase.type}`)

        this.currentPhaseIndex++

        if (this.currentPhaseIndex >= this.phases.length) {
            this.completeWorkout()
        } else {
            this.startPhase()
        }
    }

    async completeWorkout() {
        console.log('🎉 Workout completed!')

        // Record the workout
        await this.recordCompletedWorkout()

        this.showNotification('Workout completed! Great job! 🎉', 'success')
        this.stopWorkout()

        // Update home stats after completion
        this.updateHomeStats()

        // Show cool-down instructions after a short delay
        setTimeout(() => {
            this.showCooldownInstructions()
        }, 2000)
    }

    async recordCompletedWorkout() {
        try {
            const workoutData = {
                programId: this.currentProgram?.id || 'unknown',
                sessionId: `${this.currentProgram?.id || 'unknown'}-${Date.now()}`,
                completedAt: new Date().toISOString(),
                duration: this.workoutSeconds,
                completed: true,
                metadata: {
                    programName: this.currentProgram?.name || 'Unknown Program',
                    phases: this.phases.length,
                    totalPhases: this.currentProgram?.phases?.length || 0
                }
            }

            if (this.progressTracker) {
                // Use progress tracker to record workout
                const workoutId = await this.progressTracker.recordWorkout(workoutData)
                console.log('✅ Workout recorded with ID:', workoutId)

                // Check for new achievements
                const newAchievements = await this.progressTracker.checkAchievements()
                if (newAchievements.length > 0) {
                    this.showAchievementNotifications(newAchievements)
                }

                // Reload progress data
                await this.loadProgressData()
            } else {
                // Fallback to manual tracking
                this.workoutHistory.push(workoutData)
                this.userStats.totalWorkouts++
                this.userStats.totalTime += this.workoutSeconds
                this.userStats.lastWorkoutDate = new Date().toISOString()

                // Calculate streak manually
                this.calculateStreak()

                // Save to localStorage
                await this.saveProgressData()

                console.log('✅ Workout recorded manually')
            }
        } catch (error) {
            console.error('❌ Failed to record workout:', error)
            this.showNotification('Workout completed but failed to save progress', 'warning')
        }
    }

    calculateStreak() {
        // Simple streak calculation for fallback
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        const recentWorkouts = this.workoutHistory
            .filter(w => w.completed)
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))

        let streak = 0
        let checkDate = new Date(today)

        for (const workout of recentWorkouts) {
            const workoutDate = new Date(workout.completedAt)
            workoutDate.setHours(0, 0, 0, 0)

            const daysDiff = Math.floor((checkDate - workoutDate) / (1000 * 60 * 60 * 24))

            if (daysDiff <= streak) {
                streak++
                checkDate = new Date(workoutDate)
            } else {
                break
            }
        }

        this.userStats.currentStreak = streak
        this.userStats.longestStreak = Math.max(this.userStats.longestStreak, streak)
    }

    showAchievementNotifications(achievements) {
        achievements.forEach((achievement, index) => {
            setTimeout(() => {
                this.showNotification(`🏆 Achievement Unlocked: ${achievement.name}!`, 'success')
            }, (index + 1) * 1500) // Stagger notifications
        })
    }

    updatePhaseDisplay() {
        const currentPhase = this.phases[this.currentPhaseIndex]
        if (!currentPhase) return

        const phaseElapsed = this.workoutSeconds - this.phaseStartTime
        const phaseRemaining = Math.max(0, currentPhase.duration - phaseElapsed)

        // Update phase info
        const currentPhaseEl = document.getElementById('current-phase')
        const phaseTimerEl = document.getElementById('phase-timer')
        const phaseInstructionEl = document.getElementById('phase-instruction')
        const nextPhaseInfoEl = document.getElementById('next-phase-info')
        const timerLabelEl = document.getElementById('timer-label')

        if (currentPhaseEl) {
            currentPhaseEl.textContent = `${currentPhase.type.toUpperCase()} (${this.currentPhaseIndex + 1}/${this.phases.length})`
        }

        if (phaseTimerEl) {
            const minutes = Math.floor(phaseRemaining / 60)
            const seconds = phaseRemaining % 60
            phaseTimerEl.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`
        }

        if (phaseInstructionEl) {
            phaseInstructionEl.textContent = currentPhase.instruction
        }

        if (timerLabelEl) {
            timerLabelEl.textContent = `${currentPhase.type.toUpperCase()}: ${currentPhase.instruction}`
        }

        // Show next phase info
        if (nextPhaseInfoEl) {
            const nextPhase = this.phases[this.currentPhaseIndex + 1]
            if (nextPhase) {
                nextPhaseInfoEl.textContent = `Next: ${nextPhase.type} (${nextPhase.duration}s)`
            } else {
                nextPhaseInfoEl.textContent = 'Next: Workout Complete! 🎉'
            }
        }

        // Change colors based on phase type
        if (phaseTimerEl) {
            const colors = {
                warmup: '#ff9800',
                run: '#f44336',
                walk: '#4caf50',
                cooldown: '#2196f3',
                workout: '#9c27b0'
            }
            phaseTimerEl.style.color = colors[currentPhase.type] || '#1976d2'
        }
    }

    showNotification(message, type = 'info') {
        console.log(`📢 ${type.toUpperCase()}: ${message}`)

        const notification = document.createElement('div')
        notification.textContent = message

        const colors = {
            success: '#28a745',
            warning: '#ffc107',
            error: '#dc3545',
            info: '#007bff'
        }

        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${colors[type] || colors.info};
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            z-index: 1000;
            max-width: 300px;
        `

        document.body.appendChild(notification)

        // Remove after 3 seconds
        setTimeout(() => {
            notification.remove()
        }, 3000)
    }

    // Audio methods
    playAudioCue(phase) {
        // Check if audio is enabled in settings
        const audioEnabled = document.querySelector('input[type="checkbox"]')?.checked !== false

        if (!audioEnabled) {
            console.log('🔇 Audio disabled')
            return
        }

        // Try to use Speech Synthesis API
        if ('speechSynthesis' in window) {
            this.speakInstruction(phase.instruction)
        } else {
            // Fallback to beep sounds
            this.playBeep(phase.type)
        }
    }

    speakInstruction(text) {
        try {
            // Cancel any ongoing speech
            speechSynthesis.cancel()

            const utterance = new SpeechSynthesisUtterance(text)
            utterance.rate = 0.9
            utterance.volume = 0.8
            utterance.pitch = 1.0

            // Get volume from slider if available
            const volumeSlider = document.querySelector('input[type="range"]')
            if (volumeSlider) {
                utterance.volume = volumeSlider.value / 100
            }

            speechSynthesis.speak(utterance)
            console.log(`🔊 Speaking: "${text}"`)
        } catch (error) {
            console.warn('Speech synthesis failed:', error)
            this.playBeep('default')
        }
    }

    playBeep(phaseType) {
        try {
            // Create audio context for beeps
            const audioContext = new (window.AudioContext || window.webkitAudioContext)()
            const oscillator = audioContext.createOscillator()
            const gainNode = audioContext.createGain()

            // Connect nodes
            oscillator.connect(gainNode)
            gainNode.connect(audioContext.destination)

            // Set frequency based on phase type
            const frequencies = {
                warmup: 440,    // A4
                run: 880,       // A5 (higher pitch for action)
                walk: 330,      // E4 (lower pitch for rest)
                cooldown: 523,  // C5
                workout: 660    // E5
            }

            oscillator.frequency.setValueAtTime(
                frequencies[phaseType] || 440,
                audioContext.currentTime
            )
            oscillator.type = 'sine'

            // Set volume envelope
            gainNode.gain.setValueAtTime(0, audioContext.currentTime)
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01)
            gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5)

            // Play beep
            oscillator.start(audioContext.currentTime)
            oscillator.stop(audioContext.currentTime + 0.5)

            console.log(`🔊 Playing ${phaseType} beep (${frequencies[phaseType] || 440}Hz)`)
        } catch (error) {
            console.warn('Audio beep failed:', error)
        }
    }

    // Test audio function
    testAudio() {
        console.log('🧪 Testing audio...')
        this.speakInstruction('Audio test - can you hear this?')
    }

    // PWA utility methods
    manualInstallPrompt() {
        if (this.deferredPrompt) {
            this.showInstallPrompt()
        } else {
            this.showNotification('App install not available or already installed', 'info')
        }
    }

    updateConnectionStatus() {
        const statusEl = document.getElementById('connection-status')
        if (statusEl) {
            statusEl.textContent = this.isOffline ? '📡 Offline' : '🌐 Online'
            statusEl.style.color = this.isOffline ? '#dc3545' : '#28a745'
        }
    }

    // Safety Modal Management
    createSafetyModal(title, className = '') {
        const overlay = document.createElement('div')
        overlay.className = 'safety-modal-overlay'
        overlay.innerHTML = `
            <div class="safety-modal ${className}">
                <div class="safety-header">
                    <h3 class="safety-title">${title}</h3>
                    <button class="safety-close" onclick="app.closeSafetyModal()">&times;</button>
                </div>
                <div class="safety-content"></div>
                <div class="safety-actions"></div>
            </div>
        `

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                this.closeSafetyModal()
            }
        })

        return overlay
    }

    showSafetyModal(modal) {
        document.body.appendChild(modal)
        this.activeSafetyModal = modal

        // Prevent body scroll
        document.body.style.overflow = 'hidden'
    }

    closeSafetyModal() {
        if (this.activeSafetyModal) {
            this.activeSafetyModal.remove()
            this.activeSafetyModal = null

            // Restore body scroll
            document.body.style.overflow = ''
        }
    }
}

// PWA utility functions
function showOfflineReadyMessage() {
    const notification = document.createElement('div')
    notification.textContent = '📱 App is ready for offline use!'
    notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        z-index: 1000;
        animation: slideIn 0.3s ease-out;
    `

    document.body.appendChild(notification)

    // Remove after 4 seconds
    setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease-out'
        setTimeout(() => notification.remove(), 300)
    }, 4000)
}

// Add CSS animations for PWA features
const pwaStyles = document.createElement('style')
pwaStyles.textContent = `
    @keyframes slideIn {
        from {
            transform: translateX(100%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    @keyframes slideOut {
        from {
            transform: translateX(0);
            opacity: 1;
        }
        to {
            transform: translateX(100%);
            opacity: 0;
        }
    }

    @keyframes slideInUp {
        from {
            transform: translateY(100%);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    @keyframes slideOutDown {
        from {
            transform: translateY(0);
            opacity: 1;
        }
        to {
            transform: translateY(100%);
            opacity: 0;
        }
    }

    @keyframes slideInLeft {
        from {
            transform: translateX(-100%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    /* PWA Install button hover effect */
    #install-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        transition: all 0.2s ease;
    }

    /* Offline indicator pulse effect */
    #offline-indicator {
        animation: slideInLeft 0.3s ease-out, pulse 2s infinite 1s;
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
    }
`
document.head.appendChild(pwaStyles)

// Initialize the app
let app
document.addEventListener('DOMContentLoaded', async () => {
    console.log('📄 DOM Content Loaded')

    try {
        app = new SimpleApp()
        await app.init()

        // Make app globally accessible
        window.app = app

        console.log('🎉 App ready!')
    } catch (error) {
        console.error('💥 Failed to initialize app:', error)
    }
})

// Export for testing
export { SimpleApp }